import React from "react";
import axios from "axios";
import {MotionConfig} from "framer-motion";
import {UserProvider} from "./UserProvider";
import {LoginModalProvider} from "./login/LoginModalProvider";
import {AppTheme} from "./theme/AppTheme";
import {AccountBrandTheme} from "./theme/AccountBrandTheme";
import {Locale, LocaleProvider} from "./i18n";

axios.defaults.baseURL = process.env.REACT_APP_URL;

/**
 * Global context providers shared by every route (client + pre-rendered). Placed in the root layout
 * element so the context is available to all child routes during SSG and on the client.
 *
 * Theming lives in {@link AppTheme}: the light/dark mode state plus the MUI and styled-components
 * ThemeProviders (fed the same mode-matched v3 theme) and CssBaseline. {@link AccountBrandTheme} sits
 * inside `UserProvider` because it needs the account: it re-provides that theme with a signed-in Premium
 * account's brand colour (kitecorp/transferify-ui#2935).
 *
 * `MotionConfig reducedMotion="user"` (framer-motion) wraps everything so every framer-motion
 * animation honors the OS `prefers-reduced-motion` setting (ticket #142). The MUI-slot
 * `SheetTransition` reads the same media query independently, so all app motion respects the user's
 * preference from one place.
 *
 * No Stripe `<Elements>` here (perf #97): checkout is entirely Stripe-HOSTED — every paid flow
 * server-creates a Checkout/billing-portal session and full-page-redirects to it (see
 * `pricing/PricingApi.startSubscriptionCheckout` and `pay/PayApi.openBillingPortal`). Nothing in the
 * app consumes Stripe.js (`useStripe`/`useElements`/`<CardElement>`), so the old app-wide `<Elements
 * stripe={loadStripe(...)}>` only injected `js.stripe.com` (~243 KB, a 157 ms long task) into every
 * public page for no consumer. Removed outright.
 */
export default function AppProviders({children, forcedLocale}: {
	children: React.ReactNode;
	/**
	 * Pins the locale for the SSG `/ro/*` route trees (ui#101). Omitted on the runtime app, where the
	 * {@link LocaleProvider} resolves the locale from the hostname + the persisted switcher choice.
	 */
	forcedLocale?: Locale;
}) {
	return (
		<MotionConfig reducedMotion="user">
			<LocaleProvider forcedLocale={forcedLocale}>
				<AppTheme>
					<UserProvider>
						<AccountBrandTheme>
							<LoginModalProvider>
								{children}
							</LoginModalProvider>
						</AccountBrandTheme>
					</UserProvider>
				</AppTheme>
			</LocaleProvider>
		</MotionConfig>
	);
}
